import type { LucideIcon } from 'lucide-react';
import { createContext, useContext, useState, useEffect, useRef, type ReactNode } from 'react';

export interface SidebarNavItem {
  path: string;
  labelKey: string;
  icon: LucideIcon;
}

interface SidebarContextValue {
  isOpen: boolean;
  title: string;
  navItems: SidebarNavItem[];
  open: () => void;
  close: () => void;
  toggle: () => void;
  register: (title: string, items: SidebarNavItem[]) => void;
  unregister: () => void;
}

const SidebarContext = createContext<SidebarContextValue | null>(null);

export function SidebarProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [navItems, setNavItems] = useState<SidebarNavItem[]>([]);

  return (
    <SidebarContext.Provider
      value={{
        isOpen,
        title,
        navItems,
        open: () => setIsOpen(true),
        close: () => setIsOpen(false),
        toggle: () => setIsOpen((v) => !v),
        register: (t, items) => {
          setTitle(t);
          setNavItems(items);
        },
        unregister: () => {
          setTitle('');
          setNavItems([]);
          setIsOpen(false);
        },
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
}

export function useSidebar(): SidebarContextValue {
  const ctx = useContext(SidebarContext);
  if (!ctx) throw new Error('useSidebar must be used inside <SidebarProvider>');
  return ctx;
}

/**
 * Registers sidebar nav items for the lifetime of the calling component.
 * Call this once at the top of an app Layout (e.g. SafezoneLayout).
 */
export function useSidebarRegister(title: string, navItems: SidebarNavItem[]) {
  const { register, unregister } = useSidebar();
  // Stable refs so useEffect deps never change unnecessarily
  const itemsRef = useRef(navItems);
  const titleRef = useRef(title);

  useEffect(() => {
    register(titleRef.current, itemsRef.current);
    return () => unregister();
  }, [register, unregister]);
}
